/* eslint-disable @typescript-eslint/no-unused-vars, consistent-return, no-continue, no-plusplus, no-restricted-syntax, no-underscore-dangle */
// @ts-nocheck
import * as React from 'react';
import {
	Alert as ChakraAlert,
	Avatar as ChakraAvatar,
	Badge as ChakraBadge,
	Box as ChakraBox,
	Button as ChakraButton,
	Card as ChakraCard,
	Center as ChakraCenter,
	ChakraProvider as CoreChakraProvider,
	Checkbox as ChakraCheckbox,
	CloseButton as ChakraCloseButton,
	Collapsible as ChakraCollapsible,
	Dialog as ChakraDialog,
	Field as ChakraField,
	Flex as ChakraFlex,
	Heading as ChakraHeading,
	HStack as ChakraHStack,
	IconButton as ChakraIconButton,
	Image as ChakraImage,
	InputAddon as ChakraInputAddon,
	InputGroup as ChakraInputGroup,
	Input as ChakraInput,
	Link as ChakraLink,
	List as ChakraList,
	Menu as ChakraMenu,
	NativeSelect,
	PinInput as ChakraPinInput,
	Popover as ChakraPopover,
	Portal,
	Progress as ChakraProgress,
	Separator,
	SkeletonText as ChakraSkeletonText,
	SimpleGrid as ChakraSimpleGrid,
	Stack as ChakraStack,
	Switch as ChakraSwitch,
	Table as ChakraTable,
	Tabs as ChakraTabs,
	Tag as ChakraTag,
	Text as ChakraText,
	Textarea as ChakraTextarea,
	Tooltip as ChakraTooltip,
	VStack as ChakraVStack,
	chakra as chakraFactory,
	useMediaQuery as useChakraMediaQuery,
} from '@chakra-ui/react';
import {ThemeProvider} from 'next-themes';
import makeTheme from '../../theme/makeTheme';
import themeColor, {getPersonaTheme} from '../../utils/themeColors';
import {Toaster, toaster} from './toaster';

export * from '@chakra-ui/react';
export const chakra: any = chakraFactory;

const NAVIGATION_BUTTON_STYLES = {
	w: {base: 'full', md: 'auto'},
	gap: 2,
	h: 'auto',
	minH: 'auto',
	px: {base: 4, md: 4.5},
	py: 2.5,
	border: '1px solid',
	borderColor: 'transparent',
	fontWeight: '600',
	color: 'stone.700',
	fontFamily: "'Montserrat', ui-sans-serif, system-ui, sans-serif",
	letterSpacing: '0.02em',
	bg: 'transparent',
	transition: 'all 0.2s ease',
	borderRadius: 'full',
	_hover: {
		bg: 'rgba(255, 255, 255, 0.74)',
		borderColor: 'sandstone.200',
		color: 'shadow.700',
		transform: 'translateY(-1px)',
	},
	_focusVisible: {
		bg: 'rgba(255, 255, 255, 0.82)',
		borderColor: 'clay.300',
		boxShadow: '0 0 0 3px rgba(196, 148, 133, 0.25)',
		outline: 'none',
	},
	_active: {
		bg: 'marble.50',
	},
} as const;

const COLOR_SCHEME_MAP: Record<string, string> = {
	blue: themeColor(),
	gray: 'stone',
	pink: 'clay',
	primary: themeColor(),
	purple: 'blue',
	teal: themeColor(),
};

const resolveColorPalette = (colorPalette?: string, colorScheme?: string) => {
	const palette = colorPalette ?? colorScheme;

	if (!palette) {
		return undefined;
	}

	return COLOR_SCHEME_MAP[palette] ?? palette;
};

const getAccentPalette = () => themeColor();
const getUiSystem = () =>
	['auth', 'patient'].includes(getPersonaTheme().name) ? 'patient' : 'ops';

type SemanticButtonIntent = 'danger' | 'dangerSecondary' | 'primary' | 'secondary' | 'tertiary';

const resolveButtonIntent = ({
	colorPalette,
	colorScheme,
	intent,
	variant,
}: {
	colorPalette?: string;
	colorScheme?: string;
	intent?: SemanticButtonIntent;
	variant?: string;
}): SemanticButtonIntent | null => {
	if (variant === 'navigation') {
		return null;
	}

	if (intent) {
		return intent;
	}

	if (variant === 'ghost' || variant === 'link' || variant === 'plain') {
		return 'tertiary';
	}

	if (variant === 'outline') {
		return resolveColorPalette(colorPalette, colorScheme) === 'red'
			? 'dangerSecondary'
			: 'secondary';
	}

	return resolveColorPalette(colorPalette, colorScheme) === 'red' ? 'danger' : 'primary';
};

const getButtonIntentStyles = ({
	colorPalette,
	colorScheme,
	intent,
}: {
	colorPalette?: string;
	colorScheme?: string;
	intent: SemanticButtonIntent | null;
}) => {
	const accentPalette = resolveColorPalette(colorPalette, colorScheme) ?? getAccentPalette();

	switch (intent) {
		case 'danger':
			return {colorPalette: 'red', variant: 'solid'};
		case 'dangerSecondary':
			return {colorPalette: 'red', variant: 'outline'};
		case 'secondary':
			return {colorPalette: accentPalette, variant: 'outline'};
		case 'tertiary':
			return {colorPalette: accentPalette, variant: 'ghost'};
		case 'primary':
		default:
			return {colorPalette: accentPalette, variant: 'solid'};
	}
};

const normalizeTabsVariant = (variant?: string) => {
	if (variant === 'enclosed' || variant === 'page' || variant === 'soft-rounded') {
		return 'page';
	}

	if (variant === 'simple' || variant === 'subtle') {
		return 'subtle';
	}

	return undefined;
};

type LegacyBooleanProps = {
	isDisabled?: boolean;
	isInvalid?: boolean;
	isRequired?: boolean;
};

type LegacyColorSchemeProps = {
	colorScheme?: string;
};

type LegacyLineClampProps = {
	noOfLines?: number;
};

type LegacyStackProps<TProps> = TProps & {
	divider?: React.ReactElement | null;
	spacing?: React.ComponentProps<typeof ChakraStack>['gap'];
};

type CompatProps = {
	children?: React.ReactNode;
	onBlur?: (event: any) => void;
	onChange?: (event: any) => void;
	onClick?: (event: any) => void;
	onFocus?: (event: any) => void;
	onKeyDown?: (event: any) => void;
	onSubmit?: (event: any) => void;
	[key: string]: any;
};

type SemanticButtonProps = ButtonProps & {
	intent?: SemanticButtonIntent;
};

type SemanticIconButtonProps = IconButtonProps & {
	intent?: SemanticButtonIntent;
};

export type ChakraProviderProps = CompatProps & {
	theme?: ReturnType<typeof makeTheme>;
};

export type ButtonProps = CompatProps;
export type IconButtonProps = CompatProps;
export type LinkProps = CompatProps;
export type BadgeProps = CompatProps;
export type TextProps = CompatProps;
export type HeadingProps = CompatProps;
export type StackProps = CompatProps;
export type ImageProps = CompatProps;
export type InputProps = CompatProps;
export type TextareaProps = CompatProps;
export type FormControlProps = CompatProps;
export type CheckboxProps = CompatProps;
export type SwitchProps = CompatProps;
export type TooltipProps = CompatProps;
export type AlertProps = CompatProps;
export type AvatarProps = CompatProps;
export type TagProps = CompatProps;
export type TableProps = CompatProps;
export type PopoverProps = CompatProps;
export type SelectProps = CompatProps;

export const {forwardRef} = React;

const defaultTheme = makeTheme();

const createCompatMarker = (displayName: string, marker: string) => {
	const Marker = ({children}: {children?: React.ReactNode}) => <>{children}</>;
	(Marker as React.FC<{children?: React.ReactNode}> & {__compatMarker: string}).__compatMarker =
		marker;
	Marker.displayName = displayName;
	return Marker as React.FC<{children?: React.ReactNode}> & {__compatMarker: string};
};

const getCompatMarker = (child: React.ReactNode) =>
	React.isValidElement(child) && typeof child.type !== 'string'
		? (child.type as {__compatMarker?: string}).__compatMarker
		: undefined;

const normalizeBooleanProps = (props: any) => {
	const {isDisabled, isInvalid, isRequired, ...rest} = props;

	return {
		...rest,
		disabled: props.disabled ?? isDisabled,
		invalid: props.invalid ?? isInvalid,
		required: props.required ?? isRequired,
	};
};

const toArray = (children: React.ReactNode) => React.Children.toArray(children);

const withoutChildren = (props?: Record<string, any>) => {
	if (!props) {
		return props;
	}

	const {children, ...rest} = props;

	return rest;
};

const withDivider = (
	children: React.ReactNode,
	divider: React.ReactElement | null | undefined,
	orientation?: 'horizontal' | 'vertical',
) => {
	if (!divider) {
		return children;
	}

	return toArray(children).flatMap((child, index) => {
		if (index === 0) {
			return child;
		}

		return [
			React.cloneElement(divider, {
				key: `divider-${index}`,
				orientation: divider.props.orientation ?? orientation,
			}),
			child,
		];
	});
};

const buildChangeEvent = (checked: boolean | 'indeterminate', name?: string) =>
	({
		target: {
			checked,
			name,
		},
		currentTarget: {
			checked,
			name,
		},
	}) as React.ChangeEvent<HTMLInputElement>;

export const ChakraProvider = ({children, theme, ...props}: ChakraProviderProps) => (
	<CoreChakraProvider value={theme ?? defaultTheme} {...props}>
		<ThemeProvider attribute='class' defaultTheme='light' enableSystem={false}>
			{children}
			<Toaster />
		</ThemeProvider>
	</CoreChakraProvider>
);

export const Box = React.forwardRef<HTMLDivElement, any>((props, ref) => (
	<ChakraBox ref={ref} {...props} />
));

export const Flex = React.forwardRef<HTMLDivElement, any>((props, ref) => (
	<ChakraFlex ref={ref} {...props} />
));

export const Center = React.forwardRef<HTMLDivElement, any>((props, ref) => (
	<ChakraCenter ref={ref} {...props} />
));

export const Button = React.forwardRef<HTMLButtonElement, SemanticButtonProps>((props, ref) => {
	const {
		children,
		colorPalette,
		colorScheme,
		intent,
		leftIcon,
		rightIcon,
		isDisabled,
		isLoading,
		variant,
		...rest
	} = props;
	const content = toArray(children);
	const semanticIntent = resolveButtonIntent({
		colorPalette,
		colorScheme,
		intent,
		variant,
	});
	const intentStyles = getButtonIntentStyles({
		colorPalette,
		colorScheme,
		intent: semanticIntent,
	});
	const defaultVariant =
		variant == null && rest.bg == null && rest.background == null ? 'solid' : variant;

	if (leftIcon) {
		content.unshift(React.cloneElement(leftIcon, {key: 'left-icon'}));
	}

	if (rightIcon) {
		content.push(React.cloneElement(rightIcon, {key: 'right-icon'}));
	}

	return (
		<ChakraButton
			ref={ref}
			{...(variant === 'navigation' ? NAVIGATION_BUTTON_STYLES : {})}
			{...rest}
			borderRadius={rest.borderRadius ?? 'full'}
			colorPalette={intentStyles.colorPalette}
			disabled={props.disabled ?? isDisabled}
			display={rest.display ?? 'inline-flex'}
			gap={rest.gap ?? 2}
			justifyContent={rest.justifyContent ?? 'center'}
			lineHeight={rest.lineHeight ?? '1.25'}
			fontWeight={rest.fontWeight ?? '600'}
			loading={props.loading ?? isLoading}
			minH={rest.minH ?? '2.875rem'}
			px={rest.px ?? 5}
			py={rest.py ?? 3}
			variant={variant === 'navigation' ? 'plain' : (intentStyles.variant ?? defaultVariant)}
		>
			{content}
		</ChakraButton>
	);
});

export const IconButton = React.forwardRef<HTMLButtonElement, SemanticIconButtonProps>(
	(props, ref) => {
		const {
			children,
			colorPalette,
			colorScheme,
			icon,
			intent,
			isDisabled,
			isLoading,
			isRounded,
			...rest
		} = props;
		const semanticIntent = resolveButtonIntent({
			colorPalette,
			colorScheme,
			intent: intent ?? 'tertiary',
			variant: rest.variant,
		});
		const intentStyles = getButtonIntentStyles({
			colorPalette,
			colorScheme,
			intent: semanticIntent,
		});

		return (
			<ChakraIconButton
				ref={ref}
				{...rest}
				borderRadius={isRounded ? 'full' : (rest.borderRadius ?? 'full')}
				colorPalette={intentStyles.colorPalette}
				disabled={props.disabled ?? isDisabled}
				loading={props.loading ?? isLoading}
				variant={intentStyles.variant}
			>
				{children ?? icon}
			</ChakraIconButton>
		);
	},
);

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
	const {children, colorPalette, colorScheme, isExternal, rel, target, ...rest} = props;

	return (
		<ChakraLink
			ref={ref}
			{...rest}
			color={
				rest.color ??
				`${resolveColorPalette(colorPalette, colorScheme) ?? getAccentPalette()}.600`
			}
			rel={isExternal ? (rel ?? 'noopener noreferrer') : rel}
			target={isExternal ? (target ?? '_blank') : target}
			textDecorationColor='transparent'
			transition='all 0.2s ease'
		>
			{children}
		</ChakraLink>
	);
});

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>((props, ref) => {
	const {colorPalette, colorScheme, ...rest} = props;

	return (
		<ChakraBadge
			ref={ref}
			{...rest}
			borderRadius={rest.borderRadius ?? 'full'}
			colorPalette={resolveColorPalette(colorPalette, colorScheme) ?? getAccentPalette()}
			px={rest.px ?? 2.5}
			py={rest.py ?? 1}
		/>
	);
});

export const Text = React.forwardRef<HTMLParagraphElement, TextProps>((props, ref) => {
	const {lineClamp, noOfLines, ...rest} = props;

	return <ChakraText ref={ref} {...rest} lineClamp={lineClamp ?? noOfLines} />;
});

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>((props, ref) => {
	const {lineClamp, noOfLines, ...rest} = props;

	return (
		<ChakraHeading
			ref={ref}
			{...rest}
			color={rest.color ?? 'text'}
			fontWeight={rest.fontWeight ?? '600'}
			lineClamp={lineClamp ?? noOfLines}
			letterSpacing={rest.letterSpacing ?? '-0.025em'}
		/>
	);
});

export const Image = React.forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
	const {align, fit, objectFit, objectPosition, ...rest} = props;

	return (
		<ChakraImage ref={ref} {...rest} align={align ?? objectPosition} fit={fit ?? objectFit} />
	);
});

export const StackDivider = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof Separator>
>((props, ref) => <Separator ref={ref} {...props} />);

export const HStack = React.forwardRef<HTMLDivElement, any>((props, ref) => {
	const {children, divider, gap, spacing, ...rest} = props;

	return (
		<ChakraHStack ref={ref} {...rest} gap={gap ?? spacing}>
			{withDivider(children, divider, 'vertical')}
		</ChakraHStack>
	);
});

export const VStack = React.forwardRef<HTMLDivElement, any>((props, ref) => {
	const {children, divider, gap, spacing, ...rest} = props;

	return (
		<ChakraVStack ref={ref} {...rest} gap={gap ?? spacing}>
			{withDivider(children, divider, 'horizontal')}
		</ChakraVStack>
	);
});

export const Stack = React.forwardRef<HTMLDivElement, StackProps>((props, ref) => {
	const {children, direction, divider, gap, spacing, ...rest} = props;
	const orientation = direction === 'row' ? 'vertical' : 'horizontal';

	return (
		<ChakraStack ref={ref} {...rest} direction={direction} gap={gap ?? spacing}>
			{withDivider(children, divider, orientation)}
		</ChakraStack>
	);
});

export const Table = React.forwardRef<HTMLTableElement, TableProps>((props, ref) => {
	const {children, native = true, ...rest} = props;
	const normalizedVariant = rest.variant === 'simple' ? 'outline' : rest.variant;

	return (
		<ChakraTable.Root
			ref={ref}
			{...rest}
			colorPalette={resolveColorPalette(rest.colorPalette, rest.colorScheme) ?? 'stone'}
			native={native}
			size={rest.size ?? 'sm'}
			variant={normalizedVariant ?? 'outline'}
		>
			{children}
		</ChakraTable.Root>
	);
});

type InputMarkerProps = CompatProps;

export const InputLeftElement = createCompatMarker(
	'InputLeftElement',
	'input-left-element',
) as React.FC<InputMarkerProps>;
export const InputRightElement = createCompatMarker(
	'InputRightElement',
	'input-right-element',
) as React.FC<InputMarkerProps>;
export const InputLeftAddon = createCompatMarker(
	'InputLeftAddon',
	'input-left-addon',
) as React.FC<InputMarkerProps>;
export const InputRightAddon = createCompatMarker(
	'InputRightAddon',
	'input-right-addon',
) as React.FC<InputMarkerProps>;

export const InputGroup = React.forwardRef<HTMLDivElement, any>(({children, ...rest}, ref) => {
	const parts = {
		endAddon: null as React.ReactElement<InputMarkerProps> | null,
		endElement: null as React.ReactElement<InputMarkerProps> | null,
		startAddon: null as React.ReactElement<InputMarkerProps> | null,
		startElement: null as React.ReactElement<InputMarkerProps> | null,
	};
	const content: React.ReactNode[] = [];

	for (const child of toArray(children)) {
		const marker = getCompatMarker(child);

		if (!marker || !React.isValidElement<InputMarkerProps>(child)) {
			content.push(child);
			continue;
		}

		switch (marker) {
			case 'input-left-addon':
				parts.startAddon = child;
				break;
			case 'input-left-element':
				parts.startElement = child;
				break;
			case 'input-right-addon':
				parts.endAddon = child;
				break;
			case 'input-right-element':
				parts.endElement = child;
				break;
			default:
				content.push(child);
		}
	}

	const contentChild =
		content.length === 0 ? undefined : content.length === 1 ? content[0] : <>{content}</>;

	return (
		<ChakraInputGroup
			ref={ref}
			{...rest}
			endAddon={parts.endAddon?.props.children ?? rest.endAddon}
			endAddonProps={withoutChildren(parts.endAddon?.props) ?? rest.endAddonProps}
			endElement={parts.endElement?.props.children ?? rest.endElement}
			endElementProps={withoutChildren(parts.endElement?.props) ?? rest.endElementProps}
			startAddon={parts.startAddon?.props.children ?? rest.startAddon}
			startAddonProps={withoutChildren(parts.startAddon?.props) ?? rest.startAddonProps}
			startElement={parts.startElement?.props.children ?? rest.startElement}
			startElementProps={withoutChildren(parts.startElement?.props) ?? rest.startElementProps}
		>
			{contentChild}
		</ChakraInputGroup>
	);
});

type ModalProps = CompatProps & {
	blockScrollOnMount?: boolean;
	closeOnEsc?: boolean;
	closeOnOverlayClick?: boolean;
	finalFocusRef?: React.RefObject<HTMLElement | null>;
	initialFocusRef?: React.RefObject<HTMLElement | null>;
	isCentered?: boolean;
	isOpen?: boolean;
	onClose?: () => void;
};

const DialogRootCompat = ({
	children,
	closeOnEsc,
	closeOnOverlayClick,
	blockScrollOnMount,
	finalFocusRef,
	initialFocusRef,
	isCentered,
	isOpen,
	onClose,
	...rest
}: ModalProps & {role?: 'alertdialog' | 'dialog'}) => (
	<ChakraDialog.Root
		{...rest}
		closeOnEscape={rest.closeOnEscape ?? closeOnEsc}
		closeOnInteractOutside={rest.closeOnInteractOutside ?? closeOnOverlayClick}
		finalFocusEl={
			rest.finalFocusEl ?? (finalFocusRef ? () => finalFocusRef.current : undefined)
		}
		initialFocusEl={
			rest.initialFocusEl ?? (initialFocusRef ? () => initialFocusRef.current : undefined)
		}
		open={rest.open ?? isOpen}
		onOpenChange={(details) => {
			rest.onOpenChange?.(details);
			if (!details.open) {
				onClose?.();
			}
		}}
		placement={rest.placement ?? (isCentered ? 'center' : undefined)}
		preventScroll={rest.preventScroll ?? blockScrollOnMount}
	>
		{children}
	</ChakraDialog.Root>
);

export const Modal = (props: ModalProps) => <DialogRootCompat {...props} />;

export const ModalOverlay = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraDialog.Backdrop>
>((props, ref) => (
	<Portal>
		<ChakraDialog.Backdrop ref={ref} {...props} bg={props.bg ?? 'rgba(43, 36, 31, 0.32)'} />
	</Portal>
));

export const ModalContent = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraDialog.Content>
>(({children, ...props}, ref) => (
	<Portal>
		<ChakraDialog.Positioner>
			<ChakraDialog.Content
				ref={ref}
				{...props}
				bg={props.bg ?? 'panel'}
				borderColor={props.borderColor ?? 'border'}
				borderRadius={props.borderRadius ?? 'panel'}
				boxShadow={props.boxShadow ?? '0 24px 55px rgba(79, 74, 68, 0.14)'}
				overflow='hidden'
			>
				{children}
			</ChakraDialog.Content>
		</ChakraDialog.Positioner>
	</Portal>
));

export const ModalHeader = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraDialog.Header>
>(({children, ...props}, ref) => (
	<ChakraDialog.Header
		ref={ref}
		{...props}
		borderBottom='1px solid'
		borderColor={props.borderColor ?? 'border.subtle'}
		px={props.px ?? 6}
		py={props.py ?? 5}
	>
		<ChakraDialog.Title>{children}</ChakraDialog.Title>
	</ChakraDialog.Header>
));

export const ModalBody = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraDialog.Body>
>((props, ref) => <ChakraDialog.Body ref={ref} {...props} px={props.px ?? 6} py={props.py ?? 5} />);

export const ModalFooter = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraDialog.Footer>
>((props, ref) => (
	<ChakraDialog.Footer
		ref={ref}
		{...props}
		borderTop='1px solid'
		borderColor={props.borderColor ?? 'border.subtle'}
		gap={props.gap ?? 3}
		justifyContent={props.justifyContent ?? 'flex-end'}
		px={props.px ?? 6}
		py={props.py ?? 5}
	/>
));

export const ModalCloseButton = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof ChakraDialog.CloseTrigger>
>((props, ref) => (
	<ChakraDialog.CloseTrigger {...props} asChild>
		<ChakraCloseButton ref={ref} size='sm' variant='ghost' />
	</ChakraDialog.CloseTrigger>
));

type AlertDialogProps = ModalProps & {
	leastDestructiveRef?: React.RefObject<HTMLElement | null>;
};

export const AlertDialog = ({leastDestructiveRef, ...props}: AlertDialogProps) => (
	<DialogRootCompat
		{...props}
		initialFocusRef={props.initialFocusRef ?? leastDestructiveRef}
		role='alertdialog'
	/>
);

export const AlertDialogOverlay = ModalOverlay;
export const AlertDialogContent = ModalContent;
export const AlertDialogHeader = ModalHeader;
export const AlertDialogBody = ModalBody;
export const AlertDialogFooter = ModalFooter;

type TransitionPresenceProps = CompatProps & {
	in?: boolean;
	unmountOnExit?: boolean;
};

const TransitionPresence = React.forwardRef<HTMLDivElement, TransitionPresenceProps>(
	({children, in: open, unmountOnExit, ...props}, ref) => {
		if (!open && unmountOnExit) {
			return null;
		}

		return (
			<chakraFactory.div
				ref={ref}
				{...props}
				display={open ? props.display : (props.display ?? 'none')}
			>
				{children}
			</chakraFactory.div>
		);
	},
);

export const Fade = TransitionPresence;

export const Slide = React.forwardRef<
	HTMLDivElement,
	TransitionPresenceProps & {direction?: 'bottom' | 'left' | 'right' | 'top'}
>((props, ref) => <TransitionPresence ref={ref} {...props} />);

export const SlideFade = React.forwardRef<
	HTMLDivElement,
	TransitionPresenceProps & {offsetX?: number | string; offsetY?: number | string}
>((props, ref) => <TransitionPresence ref={ref} {...props} />);

export const Collapse = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraCollapsible.Root> & {
		animateOpacity?: boolean;
		in?: boolean;
		startingHeight?: number | string;
	}
>(({children, in: open, startingHeight, ...props}, ref) => (
	<ChakraCollapsible.Root
		{...props}
		collapsedHeight={props.collapsedHeight ?? startingHeight}
		open={props.open ?? open}
	>
		<ChakraCollapsible.Content ref={ref}>{children}</ChakraCollapsible.Content>
	</ChakraCollapsible.Root>
));

type AlertMarkerProps = CompatProps;

export const AlertIcon = createCompatMarker(
	'AlertIcon',
	'alert-icon',
) as React.FC<AlertMarkerProps>;
export const AlertTitle = createCompatMarker(
	'AlertTitle',
	'alert-title',
) as React.FC<AlertMarkerProps>;
export const AlertDescription = createCompatMarker(
	'AlertDescription',
	'alert-description',
) as React.FC<AlertMarkerProps>;

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
	const {children, colorPalette, colorScheme, status, ...rest} = props;
	let iconChild: React.ReactElement<AlertMarkerProps> | null = null;
	let titleChild: React.ReactElement<AlertMarkerProps> | null = null;
	let descriptionChild: React.ReactElement<AlertMarkerProps> | null = null;
	const extras: React.ReactNode[] = [];
	const statusPalette =
		status === 'error'
			? 'red'
			: status === 'success'
				? 'green'
				: status === 'warning'
					? 'yellow'
					: (resolveColorPalette(colorPalette, colorScheme) ?? getAccentPalette());

	for (const child of toArray(children)) {
		const marker = getCompatMarker(child);

		if (!React.isValidElement<AlertMarkerProps>(child) || !marker) {
			extras.push(child);
			continue;
		}

		switch (marker) {
			case 'alert-icon':
				iconChild = child;
				break;
			case 'alert-title':
				titleChild = child;
				break;
			case 'alert-description':
				descriptionChild = child;
				break;
			default:
				extras.push(child);
		}
	}

	return (
		<ChakraAlert.Root
			ref={ref}
			{...rest}
			borderRadius={rest.borderRadius ?? '1.25rem'}
			colorPalette={statusPalette}
			variant={rest.variant ?? 'surface'}
		>
			{iconChild && (
				<ChakraAlert.Indicator {...iconChild.props}>
					{iconChild.props.children}
				</ChakraAlert.Indicator>
			)}
			{(titleChild || descriptionChild) && (
				<ChakraAlert.Content>
					{titleChild && (
						<ChakraAlert.Title {...titleChild.props}>
							{titleChild.props.children}
						</ChakraAlert.Title>
					)}
					{descriptionChild && (
						<ChakraAlert.Description {...descriptionChild.props}>
							{descriptionChild.props.children}
						</ChakraAlert.Description>
					)}
				</ChakraAlert.Content>
			)}
			{extras}
		</ChakraAlert.Root>
	);
});

export const Thead = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
	<chakraFactory.thead ref={ref} {...props} />
));

export const Tbody = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
	<chakraFactory.tbody ref={ref} {...props} />
));

export const Tfoot = React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
	<chakraFactory.tfoot ref={ref} {...props} />
));

export const Tr = React.forwardRef<HTMLTableRowElement, any>((props, ref) => (
	<chakraFactory.tr
		ref={ref}
		{...props}
		transition={props.transition ?? 'background-color 0.2s ease'}
	/>
));

export const Th = React.forwardRef<HTMLTableCellElement, any>(
	({isNumeric, textAlign, ...rest}, ref) => (
		<chakraFactory.th
			ref={ref}
			{...rest}
			borderBottomColor={rest.borderBottomColor ?? 'border'}
			color={rest.color ?? 'subtleText'}
			fontSize={rest.fontSize ?? '0.74rem'}
			fontWeight={rest.fontWeight ?? '700'}
			letterSpacing={rest.letterSpacing ?? '0.08em'}
			paddingX={rest.paddingX ?? rest.px ?? 4}
			paddingY={rest.paddingY ?? rest.py ?? 3}
			textAlign={textAlign ?? (isNumeric ? 'end' : undefined)}
			textTransform={rest.textTransform ?? 'uppercase'}
		/>
	),
);

export const Td = React.forwardRef<HTMLTableCellElement, any>(
	({isNumeric, textAlign, ...rest}, ref) => (
		<chakraFactory.td
			ref={ref}
			{...rest}
			borderBottomColor={rest.borderBottomColor ?? 'border.subtle'}
			color={rest.color ?? 'text'}
			paddingX={rest.paddingX ?? rest.px ?? 4}
			paddingY={rest.paddingY ?? rest.py ?? 3.5}
			textAlign={textAlign ?? (isNumeric ? 'end' : undefined)}
			verticalAlign={rest.verticalAlign ?? 'middle'}
		/>
	),
);

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
	const {children, fallback, name, src, srcSet, ...rest} = props;

	return (
		<ChakraAvatar.Root ref={ref} {...rest}>
			<ChakraAvatar.Fallback name={name}>{fallback}</ChakraAvatar.Fallback>
			<ChakraAvatar.Image src={src} srcSet={srcSet} />
			{children}
		</ChakraAvatar.Root>
	);
});

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>((props, ref) => {
	const {children, colorPalette, colorScheme, ...rest} = props;

	return (
		<ChakraTag.Root
			ref={ref}
			{...rest}
			borderRadius={rest.borderRadius ?? 'full'}
			colorPalette={resolveColorPalette(colorPalette, colorScheme) ?? getAccentPalette()}
			variant={rest.variant ?? 'subtle'}
		>
			<ChakraTag.Label>{children}</ChakraTag.Label>
		</ChakraTag.Root>
	);
});

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>((props, ref) => {
	const {children, hasArrow, label, placement, positioning, ...rest} = props;
	const content = label ?? rest.content;

	if (!content) {
		return children;
	}

	return (
		<ChakraTooltip.Root
			{...rest}
			positioning={placement ? {...positioning, placement} : positioning}
		>
			<ChakraTooltip.Trigger asChild>{children}</ChakraTooltip.Trigger>
			<Portal>
				<ChakraTooltip.Positioner>
					<ChakraTooltip.Content
						ref={ref}
						bg='shadow.700'
						borderRadius='xl'
						color='white'
						maxW='280px'
						px={3}
						py={2}
						textStyle='sm'
					>
						{hasArrow && (
							<ChakraTooltip.Arrow>
								<ChakraTooltip.ArrowTip />
							</ChakraTooltip.Arrow>
						)}
						{content}
					</ChakraTooltip.Content>
				</ChakraTooltip.Positioner>
			</Portal>
		</ChakraTooltip.Root>
	);
});

export const Popover = ({
	children,
	isOpen,
	onClose,
	placement,
	positioning,
	...rest
}: PopoverProps) => (
	<ChakraPopover.Root
		{...rest}
		open={rest.open ?? isOpen}
		onOpenChange={(details) => {
			rest.onOpenChange?.(details);
			if (!details.open) {
				onClose?.();
			}
		}}
		positioning={placement ? {...positioning, placement} : positioning}
	>
		{children}
	</ChakraPopover.Root>
);

export const PopoverTrigger = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof ChakraPopover.Trigger>
>(({children, ...props}, ref) => (
	<ChakraPopover.Trigger ref={ref} {...props} asChild>
		{children}
	</ChakraPopover.Trigger>
));

export const PopoverContent = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraPopover.Content>
>(({children, ...props}, ref) => (
	<Portal>
		<ChakraPopover.Positioner>
			<ChakraPopover.Content
				ref={ref}
				{...props}
				bg={props.bg ?? 'panel'}
				borderColor={props.borderColor ?? 'border'}
				borderRadius={props.borderRadius ?? 'panel'}
				boxShadow={props.boxShadow ?? '0 24px 45px rgba(79, 74, 68, 0.12)'}
			>
				{children}
			</ChakraPopover.Content>
		</ChakraPopover.Positioner>
	</Portal>
));

export const PopoverArrow = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraPopover.Arrow>
>((props, ref) => (
	<ChakraPopover.Arrow ref={ref} {...props}>
		<ChakraPopover.ArrowTip />
	</ChakraPopover.Arrow>
));

export const PopoverCloseButton = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof ChakraPopover.CloseTrigger>
>((props, ref) => (
	<ChakraPopover.CloseTrigger {...props} asChild>
		<ChakraCloseButton ref={ref} size='sm' />
	</ChakraPopover.CloseTrigger>
));

export const PopoverHeader = ChakraPopover.Header;
export const PopoverBody = ChakraPopover.Body;

export const Menu = ChakraMenu.Root;

export const MenuButton = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<typeof ChakraMenu.Trigger>
>(({children, ...props}, ref) => (
	<ChakraMenu.Trigger ref={ref} {...props}>
		{children}
	</ChakraMenu.Trigger>
));

export const MenuList = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraMenu.Content>
>(({children, ...props}, ref) => (
	<Portal>
		<ChakraMenu.Positioner>
			<ChakraMenu.Content
				ref={ref}
				{...props}
				bg={props.bg ?? 'panel'}
				borderColor={props.borderColor ?? 'border'}
				borderRadius={props.borderRadius ?? '1.25rem'}
				boxShadow={props.boxShadow ?? '0 24px 45px rgba(79, 74, 68, 0.12)'}
				p={props.p ?? 2}
			>
				{children}
			</ChakraMenu.Content>
		</ChakraMenu.Positioner>
	</Portal>
));

export const MenuItem = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraMenu.Item> & {
		as?: React.ElementType;
		icon?: React.ReactNode;
	}
>(({as, children, icon, value, ...props}, ref) => {
	const generatedValue = React.useId();
	const content = (
		<>
			{icon}
			{children}
		</>
	);

	if (as) {
		const Component = as;

		return (
			<ChakraMenu.Item ref={ref} value={value ?? generatedValue} asChild>
				<Component {...props}>{content}</Component>
			</ChakraMenu.Item>
		);
	}

	return (
		<ChakraMenu.Item
			ref={ref}
			{...props}
			borderRadius={props.borderRadius ?? 'xl'}
			gap={props.gap ?? 3}
			px={props.px ?? 3}
			py={props.py ?? 2.5}
			value={value ?? generatedValue}
		>
			{content}
		</ChakraMenu.Item>
	);
});

export const MenuDivider = ChakraMenu.Separator;

const TabsCountersContext = React.createContext<{
	colorPalette?: string;
	legacyVariant?: 'page' | 'subtle';
	panelIndex: React.MutableRefObject<number>;
	tabIndex: React.MutableRefObject<number>;
	uiSystem: 'ops' | 'patient';
} | null>(null);

export const Tabs = ({
	children,
	colorPalette,
	colorScheme,
	defaultIndex,
	index,
	isLazy,
	onChange,
	variant,
	...props
}: any) => {
	const tabIndex = React.useRef(0);
	const panelIndex = React.useRef(0);
	const normalizedVariant = normalizeTabsVariant(variant);
	const uiSystem = getUiSystem();
	const resolvedColorPalette =
		resolveColorPalette(colorPalette, colorScheme) ??
		(normalizedVariant ? getAccentPalette() : undefined);
	const resolvedDefaultValue =
		props.defaultValue ??
		(defaultIndex != null
			? String(defaultIndex)
			: props.value == null && index == null
				? '0'
				: undefined);

	tabIndex.current = 0;
	panelIndex.current = 0;

	return (
		<TabsCountersContext.Provider
			value={{
				colorPalette: resolvedColorPalette,
				legacyVariant: normalizedVariant,
				panelIndex,
				tabIndex,
				uiSystem,
			}}
		>
			<ChakraTabs.Root
				{...props}
				colorPalette={resolvedColorPalette}
				defaultValue={resolvedDefaultValue}
				fitted={props.fitted ?? props.isFitted}
				lazyMount={props.lazyMount ?? isLazy}
				onValueChange={(details) => {
					props.onValueChange?.(details);
					onChange?.(Number(details.value));
				}}
				unmountOnExit={props.unmountOnExit ?? isLazy}
				variant={normalizedVariant ? 'plain' : variant}
				value={props.value ?? (index != null ? String(index) : undefined)}
			>
				{children}
			</ChakraTabs.Root>
		</TabsCountersContext.Provider>
	);
};

export const TabList = React.forwardRef<HTMLDivElement, any>(({gap, ...props}, ref) => {
	const context = React.useContext(TabsCountersContext);
	const isPageTabs = context?.legacyVariant === 'page';
	const isSubtleTabs = context?.legacyVariant === 'subtle';
	const isPatientUi = context?.uiSystem === 'patient';

	return (
		<ChakraTabs.List
			ref={ref}
			{...props}
			alignItems={props.alignItems ?? 'center'}
			bg={
				props.bg ??
				(isPageTabs
					? isPatientUi
						? 'rgba(248, 225, 216, 0.34)'
						: 'rgba(245, 241, 235, 0.78)'
					: undefined)
			}
			borderBottom={
				props.borderBottom ??
				(isSubtleTabs ? '1px solid' : isPageTabs ? '1px solid' : undefined)
			}
			borderColor={props.borderColor ?? (isPageTabs || isSubtleTabs ? 'border' : undefined)}
			borderRadius={
				props.borderRadius ?? (isPageTabs ? (isPatientUi ? 'full' : 'xl') : undefined)
			}
			gap={gap ?? (isPageTabs || isSubtleTabs ? 2 : undefined)}
			p={props.p ?? (isPageTabs ? 1 : undefined)}
		/>
	);
});

export const Tab = React.forwardRef<HTMLButtonElement, any>(
	({children, isDisabled, value, _hover, _selected, ...props}, ref) => {
		const context = React.useContext(TabsCountersContext);
		const nextIndex = context ? context.tabIndex.current++ : 0;
		const generatedValue = value ?? String(nextIndex);
		const palette = context?.colorPalette ?? 'blue';
		const isPatientUi = context?.uiSystem === 'patient';
		const compatProps =
			context?.legacyVariant === 'page'
				? {
						bg: props.bg ?? 'transparent',
						border: '1px solid',
						borderColor: 'transparent',
						borderRadius: props.borderRadius ?? (isPatientUi ? 'full' : 'lg'),
						boxShadow: 'none',
						color: props.color ?? 'stone.700',
						fontWeight: props.fontWeight ?? '600',
						outline: 'none',
						px: props.px ?? 4,
						py: props.py ?? 2.5,
						_hover: {
							bg: isPatientUi ? 'rgba(255, 253, 249, 0.84)' : 'white',
							borderColor: 'border',
							boxShadow: 'none',
							color: `${palette}.600`,
							..._hover,
						},
						_focusVisible: {
							boxShadow: 'none',
							outline: 'none',
						},
						_selected: {
							bg: isPatientUi
								? `linear-gradient(180deg, color-mix(in srgb, var(--chakra-colors-${palette}-100) 72%, white) 0%, rgba(255, 253, 249, 0.96) 100%)`
								: 'white',
							borderColor: `${palette}.200`,
							boxShadow: isPatientUi
								? '0 10px 18px rgba(180, 138, 120, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.72)'
								: '0 8px 18px rgba(79, 74, 68, 0.08)',
							color: `${palette}.700`,
							outline: 'none',
							..._selected,
						},
					}
				: context?.legacyVariant === 'subtle'
					? {
							bg: 'transparent',
							borderBottom: '2px solid',
							borderBottomColor: 'transparent',
							borderRadius: '0',
							color: props.color ?? 'stone.600',
							fontWeight: props.fontWeight ?? '600',
							px: props.px ?? 3.5,
							py: props.py ?? 2.5,
							_hover: {
								bg: 'transparent',
								borderBottomColor: 'border',
								color: `${palette}.600`,
								..._hover,
							},
							_focusVisible: {
								boxShadow: 'none',
								outline: 'none',
							},
							_selected: {
								borderBottomColor: `${palette}.400`,
								color: `${palette}.700`,
								..._selected,
							},
						}
					: {
							_hover,
							_selected,
						};

		return (
			<ChakraTabs.Trigger
				ref={ref}
				{...props}
				{...compatProps}
				disabled={props.disabled ?? isDisabled}
				value={generatedValue}
			>
				{children}
			</ChakraTabs.Trigger>
		);
	},
);

export const TabPanels = ChakraTabs.ContentGroup;

export const TabPanel = React.forwardRef<HTMLDivElement, any>(
	({children, value, ...props}, ref) => {
		const context = React.useContext(TabsCountersContext);
		const nextIndex = context ? context.panelIndex.current++ : 0;
		const generatedValue = value ?? String(nextIndex);

		return (
			<ChakraTabs.Content ref={ref} {...props} value={generatedValue}>
				{children}
			</ChakraTabs.Content>
		);
	},
);

const PinInputContext = React.createContext<React.MutableRefObject<number> | null>(null);

export const PinInput = ({
	children,
	onChange,
	value,
	...props
}: React.ComponentProps<typeof ChakraPinInput.Root> & {
	onChange?: (value: string) => void;
	value?: string;
}) => {
	const inputIndex = React.useRef(0);
	inputIndex.current = 0;

	return (
		<PinInputContext.Provider value={inputIndex}>
			<ChakraPinInput.Root
				{...props}
				defaultValue={
					Array.isArray(props.defaultValue)
						? props.defaultValue
						: (props.defaultValue?.split('') ?? undefined)
				}
				onValueChange={(details) => {
					props.onValueChange?.(details);
					onChange?.(details.value.join(''));
				}}
				value={Array.isArray(value) ? value : value?.split('')}
			>
				<ChakraPinInput.Control>{children}</ChakraPinInput.Control>
			</ChakraPinInput.Root>
		</PinInputContext.Provider>
	);
};

export const PinInputField = React.forwardRef<
	HTMLInputElement,
	React.ComponentProps<typeof ChakraPinInput.Input>
>((props, ref) => {
	const context = React.useContext(PinInputContext);
	const index = context?.current ?? 0;

	if (context) {
		context.current += 1;
	}

	return <ChakraPinInput.Input ref={ref} {...props} index={props.index ?? index} />;
});

export const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => (
	<ChakraInput
		ref={ref}
		{...normalizeBooleanProps(props)}
		bg={props.bg ?? 'white'}
		borderColor={props.borderColor ?? 'border'}
		borderRadius={props.borderRadius ?? 'xl'}
		color={props.color ?? 'text'}
		_focusVisible={{
			borderColor: `${getAccentPalette()}.400`,
			boxShadow: `0 0 0 3px color-mix(in srgb, var(--chakra-colors-${getAccentPalette()}-300) 32%, transparent)`,
			...(props._focusVisible ?? {}),
		}}
		_placeholder={{
			color: 'subtleText',
			...(props._placeholder ?? {}),
		}}
	/>
));

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => (
	<ChakraTextarea
		ref={ref}
		{...normalizeBooleanProps(props)}
		bg={props.bg ?? 'white'}
		borderColor={props.borderColor ?? 'border'}
		borderRadius={props.borderRadius ?? 'xl'}
		color={props.color ?? 'text'}
		_focusVisible={{
			borderColor: `${getAccentPalette()}.400`,
			boxShadow: `0 0 0 3px color-mix(in srgb, var(--chakra-colors-${getAccentPalette()}-300) 32%, transparent)`,
			...(props._focusVisible ?? {}),
		}}
		_placeholder={{
			color: 'subtleText',
			...(props._placeholder ?? {}),
		}}
	/>
));

export const FormControl = React.forwardRef<HTMLDivElement, FormControlProps>((props, ref) => {
	const shouldForceColumnLayout =
		props.display == null &&
		props.flexDirection == null &&
		props.alignItems == null &&
		props.justifyContent == null;

	return (
		<ChakraField.Root
			ref={ref}
			{...normalizeBooleanProps(props)}
			display={props.display ?? 'flex'}
			flexDirection={props.flexDirection ?? (shouldForceColumnLayout ? 'column' : undefined)}
			gap={props.gap ?? 2.5}
		/>
	);
});

export const FormLabel = React.forwardRef<HTMLLabelElement, any>((props, ref) => (
	<ChakraField.Label
		ref={ref}
		{...props}
		color={props.color ?? 'subtleText'}
		fontSize={props.fontSize ?? '0.78rem'}
		fontWeight={props.fontWeight ?? '600'}
		letterSpacing={props.letterSpacing ?? '0.08em'}
		textTransform={props.textTransform ?? 'uppercase'}
	/>
));
export const FormErrorMessage = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraField.ErrorText>
>((props, ref) => (
	<ChakraField.ErrorText
		ref={ref}
		{...props}
		color={props.color ?? 'red.600'}
		fontSize={props.fontSize ?? 'xs'}
		mt={props.mt ?? 1}
	/>
));
export const FormHelperText = React.forwardRef<
	HTMLDivElement,
	React.ComponentProps<typeof ChakraField.HelperText>
>((props, ref) => (
	<ChakraField.HelperText
		ref={ref}
		{...props}
		color={props.color ?? 'subtleText'}
		fontSize={props.fontSize ?? 'xs'}
		mt={props.mt ?? 1}
	/>
));

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
	const {children, isChecked, isDisabled, isInvalid, isRequired, name, onChange, ...rest} = props;

	return (
		<ChakraCheckbox.Root
			{...rest}
			checked={props.checked ?? isChecked}
			disabled={props.disabled ?? isDisabled}
			invalid={props.invalid ?? isInvalid}
			required={props.required ?? isRequired}
			onCheckedChange={({checked}) => onChange?.(buildChangeEvent(checked, name))}
		>
			<ChakraCheckbox.HiddenInput ref={ref} name={name} />
			<ChakraCheckbox.Control>
				<ChakraCheckbox.Indicator />
			</ChakraCheckbox.Control>
			{children != null && <ChakraCheckbox.Label>{children}</ChakraCheckbox.Label>}
		</ChakraCheckbox.Root>
	);
});

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>((props, ref) => {
	const {children, isChecked, isDisabled, isInvalid, isRequired, name, onChange, ...rest} = props;

	return (
		<ChakraSwitch.Root
			{...rest}
			checked={props.checked ?? isChecked}
			disabled={props.disabled ?? isDisabled}
			invalid={props.invalid ?? isInvalid}
			required={props.required ?? isRequired}
			onCheckedChange={({checked}) => onChange?.(buildChangeEvent(checked, name))}
		>
			<ChakraSwitch.HiddenInput ref={ref} name={name} />
			<ChakraSwitch.Control>
				<ChakraSwitch.Thumb />
			</ChakraSwitch.Control>
			{children != null && <ChakraSwitch.Label>{children}</ChakraSwitch.Label>}
		</ChakraSwitch.Root>
	);
});

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
	const {children, icon, isDisabled, isInvalid, isRequired, size, variant, width, ...rest} =
		props;

	return (
		<NativeSelect.Root
			disabled={props.disabled ?? isDisabled}
			invalid={props.invalid ?? isInvalid}
			size={size}
			variant={variant}
			width={width}
		>
			<NativeSelect.Field
				ref={ref}
				{...rest}
				bg={rest.bg ?? 'white'}
				borderColor={rest.borderColor ?? 'border'}
				borderRadius={rest.borderRadius ?? 'xl'}
				color={rest.color ?? 'text'}
				required={props.required ?? isRequired}
			>
				{children}
			</NativeSelect.Field>
			<NativeSelect.Indicator>{icon}</NativeSelect.Indicator>
		</NativeSelect.Root>
	);
});

export const List = React.forwardRef<HTMLUListElement, any>((props, ref) => (
	<chakraFactory.ul ref={ref} {...props} />
));

export const ListItem = React.forwardRef<HTMLLIElement, any>((props, ref) => (
	<chakraFactory.li ref={ref} {...props} />
));

export const UnorderedList = List;

export const OrderedList = React.forwardRef<HTMLOListElement, any>((props, ref) => (
	<chakraFactory.ol ref={ref} {...props} />
));

export const SimpleGrid = React.forwardRef<HTMLDivElement, any>((props, ref) => {
	const {gap, spacing, ...rest} = props;

	return <ChakraSimpleGrid ref={ref} {...rest} gap={gap ?? spacing} />;
});

export const Card = React.forwardRef<HTMLDivElement, any>(({children, ...props}, ref) => (
	<ChakraCard.Root ref={ref} {...props}>
		{children}
	</ChakraCard.Root>
));

export const CardBody = ChakraCard.Body;

export const Progress = React.forwardRef<HTMLDivElement, any>((props, ref) => {
	const {isIndeterminate, value, children, ...rest} = props;

	return (
		<ChakraProgress.Root
			ref={ref}
			{...rest}
			colorPalette={
				resolveColorPalette(rest.colorPalette, rest.colorScheme) ?? getAccentPalette()
			}
			value={isIndeterminate ? null : value}
		>
			{children ?? (
				<ChakraProgress.Track>
					<ChakraProgress.Range />
				</ChakraProgress.Track>
			)}
		</ChakraProgress.Root>
	);
});

export const SkeletonText = React.forwardRef<HTMLDivElement, any>((props, ref) => {
	const {noOfLines, skeletonHeight, spacing, ...rest} = props;
	const count = noOfLines ?? 3;

	return (
		<ChakraFlex ref={ref} direction='column' gap={spacing ?? 2} {...rest}>
			{Array.from({length: count}, (_, index) => (
				<chakraFactory.div
					key={index}
					bg='bg.muted'
					borderRadius='md'
					height={skeletonHeight ?? '0.5rem'}
					opacity={0.6}
				/>
			))}
		</ChakraFlex>
	);
});

export const useDisclosure = (initialState = false) => {
	const [isOpen, setOpen] = React.useState(initialState);

	return {
		isOpen,
		open: isOpen,
		onOpen: () => setOpen(true),
		onClose: () => setOpen(false),
		onToggle: () => setOpen((current) => !current),
		setOpen,
	};
};

export const useMediaQuery = (query: string | string[]) => {
	const queries = Array.isArray(query) ? query : [query];

	return useChakraMediaQuery(queries);
};

export const useBoolean = (initialState = false) => {
	const [value, setValue] = React.useState(initialState);

	return [
		value,
		{
			on: () => setValue(true),
			off: () => setValue(false),
			toggle: () => setValue((current) => !current),
		},
	] as const;
};

export const useOutsideClick = ({
	enabled = true,
	handler,
	ref,
}: {
	enabled?: boolean;
	handler: (event: MouseEvent | TouchEvent) => void;
	ref: React.RefObject<HTMLElement | null>;
}) => {
	React.useEffect(() => {
		if (!enabled) {
			return;
		}

		const listener = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;

			if (!ref.current || (target && ref.current.contains(target))) {
				return;
			}

			handler(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [enabled, handler, ref]);
};

export const useToast =
	() =>
	({
		description,
		duration,
		isClosable,
		status,
		title,
	}: {
		description?: React.ReactNode;
		duration?: number;
		isClosable?: boolean;
		status?: 'error' | 'info' | 'loading' | 'success' | 'warning';
		title?: React.ReactNode;
	}) =>
		toaster.create({
			description,
			duration,
			closable: isClosable,
			title,
			type: status,
		});
